/**
 * Full-screen photo viewer, opened by tapping a picture in a chat.
 *
 * Shaped after WhatsApp's viewer: a black screen, the sender and when they
 * sent it in the header, download and forward on the right, and a reply bar
 * along the bottom. Before this, tapping a photo opened the raw file in a
 * new browser tab — which loses the chat, the sender and the way back.
 *
 * Escape closes, as does a click on the backdrop; the picture itself does
 * not close it, because people tap the picture to look at it.
 */

import { useEffect } from 'react'
import './Lightbox.css'

export default function Lightbox ({ open, url, who, when, onClose, onReply }) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)

    // Stop the thread behind from scrolling while the viewer is up.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label="Photo" onClick={onClose}>
      <header className="lb__bar" onClick={e => e.stopPropagation()}>
        <button type="button" className="lb__icon" onClick={onClose} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <span className="lb__who">
          <strong>{who}</strong>
          {when && <small>{when}</small>}
        </span>

        <a className="lb__icon" href={url} download target="_blank" rel="noreferrer"
           aria-label="Download" onClick={e => e.stopPropagation()}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </header>

      <div className="lb__stage" onClick={e => e.stopPropagation()}>
        <img className="lb__img" src={url} alt="" />
      </div>

      <footer className="lb__foot" onClick={e => e.stopPropagation()}>
        <button type="button" className="lb__reply" onClick={() => { onClose(); onReply?.() }}>
          Reply
        </button>
      </footer>
    </div>
  )
}
